"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildRegexFromSpans = exports.escapeRegExp = exports.normalizeForRegex = void 0;
const normalizeForRegex = (text) => text.replace(/\s+/g, ' ').trim();
exports.normalizeForRegex = normalizeForRegex;
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
exports.escapeRegExp = escapeRegExp;
const tokensWithOffsets = (msg) => {
    const out = [];
    const re = /\S+/g;
    let m;
    while ((m = re.exec(msg))) {
        out.push({ t: m[0], s: m.index, e: m.index + m[0].length });
    }
    return out;
};
/** "Current", "Alarm", "Device": a title-case word that, followed by a colon token, forms a two-word label. */
const TITLE_WORD = /^[A-Z][a-z]+$/;
const anchorsBefore = (msg, start) => {
    const toks = tokensWithOffsets(msg).filter((x) => x.e <= start);
    if (!toks.length) {
        return { atEdge: true };
    }
    const t1 = toks[toks.length - 1];
    const out = { single: t1.t };
    if (/:$/.test(t1.t) && toks.length >= 2 && TITLE_WORD.test(toks[toks.length - 2].t)) {
        out.two = `${toks[toks.length - 2].t} ${t1.t}`;
    }
    return out;
};
const anchorsAfter = (msg, end) => {
    const toks = tokensWithOffsets(msg).filter((x) => x.s >= end);
    if (!toks.length) {
        return { atEdge: true };
    }
    const t1 = toks[0];
    const out = { single: t1.t };
    if (!/:$/.test(t1.t) && toks.length >= 2 && /:$/.test(toks[1].t) && TITLE_WORD.test(t1.t)) {
        out.two = `${t1.t} ${toks[1].t}`;
    }
    return out;
};
const altOf = (set) => {
    const arr = [...set].map(exports.escapeRegExp);
    return arr.length === 1 ? arr[0] : `(?:${arr.join('|')})`;
};
const quoteAll = (set) => [...set].map((x) => `"${x}"`).join(' or ');
const verify = (regex, examples) => {
    const problems = [];
    examples.forEach((ex, i) => {
        let m = null;
        try {
            m = new RegExp(regex, 'i').exec(ex.message);
        }
        catch (error) {
            problems.push(`invalid regex: ${error.message}`);
            return;
        }
        if (!m) {
            problems.push(`example ${i + 1}: the generated regex does not match it`);
            return;
        }
        ex.spans.forEach((sp) => {
            const want = ex.message.slice(sp.start, sp.end).trim();
            const got = ((m && m.groups && m.groups[sp.tag]) || '').trim();
            if (want !== got) {
                problems.push(`example ${i + 1}: <${sp.tag}> would capture "${got}" instead of "${want}"`);
            }
        });
    });
    return problems;
};
const buildRegexFromSpans = (examples) => {
    const byTag = {};
    const order = [];
    examples.forEach((ex) => {
        ex.spans
            .slice()
            .sort((a, b) => a.start - b.start)
            .forEach((sp) => {
            if (!byTag[sp.tag]) {
                byTag[sp.tag] = [];
                order.push(sp.tag);
            }
            byTag[sp.tag].push({
                text: ex.message.slice(sp.start, sp.end).trim(),
                before: anchorsBefore(ex.message, sp.start),
                after: anchorsAfter(ex.message, sp.end),
            });
        });
    });
    if (!order.length) {
        return { regex: '', tags: [], problems: [], explain: [] };
    }
    const optionsFor = (tag) => {
        const items = byTag[tag];
        const multi = items.some((it) => /\s/.test(it.text));
        const numeric = items.every((it) => /^\d+$/.test(it.text));
        const befores = [];
        if (items.every((it) => it.before.atEdge)) {
            befores.push({ re: '^', why: 'from the start' });
        }
        else if (items.some((it) => it.before.atEdge)) {
            befores.push({ re: '', why: 'anywhere' });
        }
        else {
            const singles = new Set(items.map((it) => it.before.single));
            befores.push({ re: `${altOf(singles)}\\s*`, why: `after ${quoteAll(singles)}` });
            if (items.some((it) => it.before.two)) {
                const longest = new Set(items.map((it) => it.before.two || it.before.single));
                befores.push({ re: `${altOf(longest)}\\s*`, why: `after ${quoteAll(longest)}` });
            }
        }
        const afters = [];
        if (!multi) {
            afters.push({ re: '', pat: numeric ? '\\d+' : '\\S+', why: 'one word' });
        }
        else if (items.every((it) => it.after.atEdge)) {
            afters.push({ re: '', pat: '.*', why: 'to the end' });
        }
        else {
            const has = items.filter((it) => !it.after.atEdge);
            const longest = new Set(has.map((it) => it.after.two || it.after.single));
            afters.push({ re: `(?=\\s*${altOf(longest)})`, pat: '.*?', why: `up to ${quoteAll(longest)}` });
            if (has.some((it) => it.after.two)) {
                const singles = new Set(has.map((it) => it.after.single));
                afters.push({ re: `(?=\\s*${altOf(singles)})`, pat: '.*?', why: `up to ${quoteAll(singles)}` });
            }
        }
        const combos = [];
        befores.forEach((b) => afters.forEach((a) => combos.push({ piece: `${b.re}(?<${tag}>${a.pat})${a.re}`, why: `${tag}: ${b.why}, ${a.why}` })));
        return combos;
    };
    const perTag = order.map(optionsFor);
    let best = null;
    const walk = (i, pieces, whys) => {
        if (best && best.problems.length === 0) {
            return;
        }
        if (i === order.length) {
            const regex = pieces.join('.*?');
            const problems = verify(regex, examples);
            if (!best || problems.length < best.problems.length) {
                best = { regex, problems, explain: whys.slice() };
            }
            return;
        }
        perTag[i].forEach((opt) => walk(i + 1, pieces.concat([opt.piece]), whys.concat([opt.why])));
    };
    walk(0, [], []);
    const chosen = best;
    return { regex: chosen.regex, tags: order, problems: chosen.problems, explain: chosen.explain };
};
exports.buildRegexFromSpans = buildRegexFromSpans;
//# sourceMappingURL=regexByExample.js.map